import { QuantityType, RepresentationType, ThumbnailType } from '../../../../bom/constants';
import React, { useEffect, useRef, useState } from 'react';
import { SortOrder, sortBasedOnRecentBomOptionsList } from '../../utils';
import { getMostRecentlySelectedBomOption, getRecentBomOptionsList } from '../../../product/selectors';
import { getQuantityConfig, getQuestions } from '../../../../bom/selectors';
import { useCurrentEditedQuantityCardScrollIntoView, useCurrentEditedQuantityInputGetFocused } from '../../hooks';

import AlertWrapper from '../../../common/AlertWrapper';
import Card from 'react-bootstrap/Card';
import ChildOptionQuantityEditor from '../ChildOptionQuantityEditor';
import Form from 'react-bootstrap/Form';
import OptionName from '../OptionName';
import PropTypes from 'prop-types';
import { QuantityGroupAlertId } from '../../constants';
import RemoveButton from '../RemoveButton';
import StepNumber from '../StepNumber';
import { formatQuantity } from '../../../../utils/formatter';
import { getMinimumQuantity } from '../../../../price/selectors';
import styles from './ChildQuantityGroup.scss';
import { useSelector } from 'react-redux';

const ChildQuantityGroup = ({
  childQuestion,
  feedback,
  parentOptions,
  parentQuestion,
  resetFeedback,
  showPreviousSelectionNotAvailableWarning = false,
  onRemoveOption,
}) => {
  const mostRecentlySelectedBomOption = useSelector((state) =>
    getMostRecentlySelectedBomOption(state, parentQuestion.code)
  );
  const questions = useSelector(getQuestions);
  const recentBomOptionsList = useSelector(getRecentBomOptionsList);
  const qtyConfig = useSelector(getQuantityConfig);
  const miniQty = useSelector(getMinimumQuantity);

  const [warningShowingStatus, setWarningShowingStatus] = useState(
    Object.fromEntries(
      parentOptions.map((option) => [
        option.codeValue,
        showPreviousSelectionNotAvailableWarning &&
          !!mostRecentlySelectedBomOption &&
          mostRecentlySelectedBomOption.codeValue === option.codeValue,
      ])
    )
  );

  let childQuestionLabel = (childQuestion.questionLabel || '').trim();
  if (!childQuestionLabel.endsWith(':')) childQuestionLabel += ':';

  const alertShouldScrollIntoView =
    questions.length > 0 &&
    questions.every((question) => question.representationType === RepresentationType.PARENT_CHILD) &&
    !!feedback['quantity'];
  const selectedParentOptions = sortBasedOnRecentBomOptionsList(
    parentOptions.filter((option) => option.isSelected),
    recentBomOptionsList,
    SortOrder.Asc
  );
  const hasNoColorReferences = parentOptions.every((option) => !option.colorReference);
  const needToUseTexture = parentOptions.some((option) => option.useTexture);
  const showRemoveButton = parentOptions.filter((option) => !option.isSoldOut).length > 1;

  const alertRef = useRef(null);
  const [quantityCardRef, currentEditedQuantityCardIndex] =
    useCurrentEditedQuantityCardScrollIntoView(selectedParentOptions);
  const [quantityInputRef, currentEditedQuantityInputIndex] =
    useCurrentEditedQuantityInputGetFocused(selectedParentOptions);

  useEffect(() => {
    setWarningShowingStatus(
      Object.fromEntries(
        parentOptions.map((option) => [
          option.codeValue,
          showPreviousSelectionNotAvailableWarning &&
            !!mostRecentlySelectedBomOption &&
            mostRecentlySelectedBomOption.codeValue === option.codeValue,
        ])
      )
    );
  }, [mostRecentlySelectedBomOption, parentOptions, showPreviousSelectionNotAvailableWarning]);

  useEffect(() => {
    if (alertShouldScrollIntoView && !!alertRef && !!alertRef.current) {
      alertRef.current.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'nearest' });
    }
  }, [alertShouldScrollIntoView]);

  const handleCloseWarning = (option) => {
    setWarningShowingStatus({ ...warningShowingStatus, [option.codeValue]: false });
  };

  const handleRemove = (parentOption) => {
    if (!!onRemoveOption && typeof onRemoveOption === 'function') {
      onRemoveOption(parentOption);
    }
  };

  return (
    <Form.Group className={`${styles['child-quantity-group']} mb-3`}>
      <div className={styles['child-question-label']}>
        <StepNumber step={`${parentQuestion.code}-quantity`} />
        {childQuestionLabel}
      </div>
      <div className={styles['quantity-description']}>
        {qtyConfig.type !== QuantityType.Common && (
          <span>
            {qtyConfig.type === QuantityType.Exact
              ? 'This product can only be ordered in the exact quantities shown in the price block.'
              : `This product can only be ordered in groups of ${formatQuantity(qtyConfig.minimumDivisor)}.`}
          </span>
        )}
        <span>{`Your total order must be minimum of ${formatQuantity(miniQty)}.`}</span>
      </div>
      <AlertWrapper id={QuantityGroupAlertId} ref={alertRef} show={!!feedback['quantity']} variant="danger">
        {feedback['quantity']}
      </AlertWrapper>
      <div className={styles['quantity-cards-holder']}>
        {selectedParentOptions.map((option, index) => (
          <Card key={option.codeValue} ref={index === currentEditedQuantityCardIndex ? quantityCardRef : undefined}>
            <Card.Header>
              <OptionName
                option={option}
                questionId={parentQuestion.questionId}
                thumbnailType={
                  parentQuestion.thumbnailType === ThumbnailType.LOCAL_IMAGE && hasNoColorReferences
                    ? ThumbnailType.LOCAL_IMAGE
                    : parentQuestion.thumbnailType === ThumbnailType.SCENE7_IMAGE &&
                      (hasNoColorReferences || needToUseTexture)
                    ? ThumbnailType.SCENE7_IMAGE
                    : ThumbnailType.COLOR_SWATCH
                }
              />
              {showRemoveButton && (
                <RemoveButton aria-label={`Remove ${option.description}`} onClick={() => handleRemove(option)} />
              )}
            </Card.Header>
            <Card.Body>
              <AlertWrapper
                dismissible
                show={warningShowingStatus[option.codeValue]}
                variant="warning"
                onClose={() => handleCloseWarning(option)}>
                One of your previous options is unavailable and has been removed from your current selection.
              </AlertWrapper>
              <ChildOptionQuantityEditor
                childQuestion={childQuestion}
                closePreviousSelectionNotAvailableWarning={
                  warningShowingStatus[option.codeValue] ? () => handleCloseWarning(option) : undefined
                }
                feedback={feedback}
                parentOption={option}
                ref={index === currentEditedQuantityInputIndex ? quantityInputRef : undefined}
                resetFeedback={resetFeedback}
              />
            </Card.Body>
          </Card>
        ))}
      </div>
    </Form.Group>
  );
};

const QuestionShape = PropTypes.shape({
  code: PropTypes.string.isRequired,
  isRealParentChildQuestion: PropTypes.bool.isRequired,
  itemId: PropTypes.string.isRequired,
  mandatoryFlag: PropTypes.string.isRequired,
  needToReloadUniqueOptions: PropTypes.bool.isRequired,
  questionId: PropTypes.number.isRequired,
  questionLabel: PropTypes.string.isRequired,
  questionText: PropTypes.string.isRequired,
  questionType: PropTypes.oneOf([0, 1, 2, 3]).isRequired,
  representationType: PropTypes.oneOf([0, 1]).isRequired,
  sampleQuestionText: PropTypes.string,
  selectionType: PropTypes.oneOf([0, 1]).isRequired,
  sequence: PropTypes.number.isRequired,
  thumbnailType: PropTypes.oneOf([0, 1, 2, 3]).isRequired,
});

ChildQuantityGroup.propTypes = {
  childQuestion: QuestionShape.isRequired,
  feedback: PropTypes.objectOf(PropTypes.string).isRequired,
  parentOptions: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string.isRequired,
      codeValue: PropTypes.string.isRequired,
      colorGroupList: PropTypes.arrayOf(PropTypes.string).isRequired,
      colorReference: PropTypes.string.isRequired,
      componentId: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      estimatedShipDate: PropTypes.string.isRequired,
      futureShipDateSettingStatus: PropTypes.oneOf([0, 1, 2]).isRequired,
      helpLink: PropTypes.shape({
        helpText: PropTypes.string.isRequired,
        linkText: PropTypes.string.isRequired,
      }),
      inventoryMessage: PropTypes.string.isRequired,
      isFutureShipDateSettable: PropTypes.bool.isRequired,
      isOutOfStock: PropTypes.bool.isRequired,
      isSelected: PropTypes.bool.isRequired,
      isSoldOut: PropTypes.bool.isRequired,
      itemId: PropTypes.string.isRequired,
      optionText: PropTypes.string.isRequired,
      outOfStockMessage: PropTypes.string.isRequired,
      quantity: PropTypes.number.isRequired,
      sequence: PropTypes.number.isRequired,
      specialBomCharge: PropTypes.string.isRequired,
      useTexture: PropTypes.bool.isRequired,
      yearOptionPricing: PropTypes.oneOf([0, 1, 2]).isRequired,
    })
  ).isRequired,
  parentQuestion: QuestionShape.isRequired,
  resetFeedback: PropTypes.func.isRequired,
  showPreviousSelectionNotAvailableWarning: PropTypes.bool,
  onRemoveOption: PropTypes.func,
};

export default ChildQuantityGroup;
